import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { environment } from '#environments';

export interface NetworkGraphNode {
  id: string;
  label?: string | null;
  type?: string | null;
  name?: string | null;
  ecCode?: string | null;
  jdeCode?: string | null;
  jkt?: string | null;
  facility?: string | null;
  wellType?: string | null;
  equipmentCode?: string | null;
  description?: string | null;
  facilityCode?: string | null;
  adxTagCount?: number | null;
  analogCount?: number | null;
  digitalCount?: number | null;
  [key: string]: unknown;
}

export interface NetworkGraphLink {
  source: string;
  target: string;
  type?: string | null;
  relationship?: string | null;
  [key: string]: unknown;
}

export interface NetworkGraphPayload {
  nodes: NetworkGraphNode[];
  links: NetworkGraphLink[];
  createdDate?: string | null;
}

interface NetworkGraphNodeDto {
  id: unknown;
  [key: string]: unknown;
}

interface NetworkGraphLinkDto {
  source: unknown;
  target: unknown;
  [key: string]: unknown;
}

interface NetworkGraphPayloadDto {
  nodes?: NetworkGraphNodeDto[] | null;
  links?: NetworkGraphLinkDto[] | null;
  createdDate?: string | null;
  createddate?: string | null;
  CreatedDate?: string | null;
}

interface NetworkDiagramMetadataDto {
  name?: string | null;
  createdDate?: string | null;
  CreatedDate?: string | null;
  updatedDate?: string | null;
  UpdatedDate?: string | null;
}

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private _http = inject(HttpClient);

  getOkanNetworkDiagram(): Observable<NetworkGraphPayload | null> {
    if (environment.useMockData) {
      return of(null);
    }

    return forkJoin({
      payload: this._http.get<NetworkGraphPayloadDto>(`${environment.apiUrl}/networkdiagram/okan`),
      metadata: this._http
        .get<NetworkDiagramMetadataDto[]>(`${environment.apiUrl}/networkdiagram`)
        .pipe(catchError(() => of<NetworkDiagramMetadataDto[]>([]))),
    }).pipe(
      map(({ payload, metadata }) => this.mapNetworkGraph(payload, this.resolveMetadataCreatedDate(metadata, 'okan')))
    );
  }

  private mapNetworkGraph(
    dto: NetworkGraphPayloadDto | null | undefined,
    metadataCreatedDate?: string | null,
  ): NetworkGraphPayload {
    const sourceNodes = Array.isArray(dto?.nodes) ? dto?.nodes : [];
    const sourceLinks = Array.isArray(dto?.links) ? dto?.links : [];

    const seenNodeIds = new Set<string>();
    const nodes: NetworkGraphNode[] = [];
    for (const sourceNode of sourceNodes) {
      const id = this.normalizeId(sourceNode?.id);
      if (!id || seenNodeIds.has(id)) {
        continue;
      }
      seenNodeIds.add(id);
      nodes.push({ ...sourceNode, id });
    }

    const validNodeIds = new Set(nodes.map(node => node.id));
    const links: NetworkGraphLink[] = [];
    for (const sourceLink of sourceLinks) {
      const source = this.normalizeId(sourceLink?.source);
      const target = this.normalizeId(sourceLink?.target);
      if (!source || !target || !validNodeIds.has(source) || !validNodeIds.has(target)) {
        continue;
      }
      links.push({ ...sourceLink, source, target });
    }

    return {
      nodes,
      links,
      createdDate: metadataCreatedDate ?? this.resolveCreatedDate(dto),
    };
  }

  private resolveMetadataCreatedDate(
    diagrams: NetworkDiagramMetadataDto[] | null | undefined,
    name: string,
  ): string | null {
    const normalizedName = name.trim().toLowerCase();
    const match = (diagrams ?? []).find(diagram => String(diagram?.name ?? '').trim().toLowerCase() === normalizedName);
    if (!match) {
      return null;
    }

    // A scheduled refresh overwrites the diagram, so the last update is its "as of" time.
    const value = [match.updatedDate, match.UpdatedDate, match.createdDate, match.CreatedDate]
      .map(candidate => String(candidate ?? '').trim())
      .find(candidate => candidate.length > 0);
    return value ?? null;
  }

  private resolveCreatedDate(dto: NetworkGraphPayloadDto | null | undefined): string | null {
    if (!dto) {
      return null;
    }

    const value = dto.createdDate ?? dto.createddate ?? dto.CreatedDate ?? null;
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  private normalizeId(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : null;
  }
}